import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiUser, FiGlobe, FiTwitter, FiInstagram, FiYoutube, FiExternalLink, FiMail, FiTag, FiCalendar, FiEye } = FiIcons

const PublicProfile = () => {
  const { username } = useParams()
  const [profile, setProfile] = useState(null)
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadPublicProfile()
  }, [username])

  const loadPublicProfile = async () => {
    try {
      setLoading(true)
      const { supabase } = await import('../lib/supabase')
      
      // Load user profile by username or ID
      let userQuery = supabase
        .from('users')
        .select('*')
        .eq('is_public', true)
      
      // Check if username is a UUID (direct ID) or username
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(username)
      
      if (isUUID) {
        userQuery = userQuery.eq('id', username)
      } else {
        userQuery = userQuery.eq('username', username)
      }
      
      const { data: userData, error: userError } = await userQuery.single()
      
      if (userError || !userData) {
        setError('Profile not found or not public')
        return
      }
      
      setProfile(userData)
      
      // Load user's public projects
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select('*')
        .eq('owner_id', userData.id)
        .eq('status', 'published')
        .eq('is_private', false)
        .order('updated_at', { ascending: false })
      
      if (!projectsError) {
        setProjects(projectsData || [])
      }
      
    } catch (err) {
      console.error('Error loading public profile:', err)
      setError('Error loading profile')
    } finally {
      setLoading(false)
    }
  }

  const getYouTubeVideoId = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)
    return match ? match[1] : null
  }

  const getSocialUrl = (platform, value) => {
    if (!value) return null
    
    if (value.startsWith('http')) return value
    
    const username = value.replace('@', '')
    const urls = {
      twitter: `https://twitter.com/${username}`,
      instagram: `https://instagram.com/${username}`,
      youtube: value
    }
    
    return urls[platform] || value
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiUser} className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h1>
          <p className="text-gray-600">This profile doesn't exist or is not public.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-start space-x-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {profile.avatar_url ? (
                <img 
                  src={profile.avatar_url} 
                  alt={profile.display_name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-100 shadow-lg"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center border-4 border-gray-100 shadow-lg">
                  <SafeIcon icon={FiUser} className="w-16 h-16 text-white" />
                </div>
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {profile.display_name || 'Filmmaker'}
              </h1>
              
              {/* Professional Tags */}
              {profile.professional_tags && profile.professional_tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {profile.professional_tags.map((tag) => (
                    <div
                      key={tag}
                      className="flex items-center space-x-1 bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-medium"
                    >
                      <SafeIcon icon={FiTag} className="w-3 h-3" />
                      <span>{tag}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center space-x-4 text-gray-600 mb-4">
                {profile.show_email && (
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiMail} className="w-4 h-4" />
                    <span>{profile.email}</span>
                  </div>
                )}
                <div className="flex items-center space-x-1">
                  <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                  <span>Joined {formatDate(profile.created_at)}</span>
                </div>
              </div>
              
              {profile.bio && (
                <p className="text-gray-700 text-lg leading-relaxed mb-6 max-w-2xl">{profile.bio}</p>
              )}
              
              {/* Social Links */}
              <div className="flex flex-wrap items-center gap-4">
                {profile.website && (
                  <a
                    href={getSocialUrl('website', profile.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    <SafeIcon icon={FiGlobe} className="w-5 h-5" />
                    <span>Website</span>
                    <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  </a>
                )}
                
                {profile.twitter && (
                  <a
                    href={getSocialUrl('twitter', profile.twitter)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg text-blue-700 hover:text-blue-900 transition-colors"
                  >
                    <SafeIcon icon={FiTwitter} className="w-5 h-5" />
                    <span>Twitter</span>
                    <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  </a>
                )}
                
                {profile.instagram && (
                  <a
                    href={getSocialUrl('instagram', profile.instagram)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-pink-100 hover:bg-pink-200 rounded-lg text-pink-700 hover:text-pink-900 transition-colors"
                  >
                    <SafeIcon icon={FiInstagram} className="w-5 h-5" />
                    <span>Instagram</span>
                    <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  </a>
                )}
                
                {profile.youtube && (
                  <a
                    href={getSocialUrl('youtube', profile.youtube)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-red-100 hover:bg-red-200 rounded-lg text-red-700 hover:text-red-900 transition-colors"
                  >
                    <SafeIcon icon={FiYoutube} className="w-5 h-5" />
                    <span>YouTube</span>
                    <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Showreel */}
        {profile.showreel_url && getYouTubeVideoId(profile.showreel_url) && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiYoutube} className="w-6 h-6 text-red-600" />
                <span>Showreel</span>
              </h2>
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(profile.showreel_url)}`}
                  title="Showreel"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        )}

        {/* Public Projects */}
        {projects.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                <SafeIcon icon={FiEye} className="w-6 h-6 text-primary-600" />
                <span>Public Projects</span>
                <span className="text-sm font-normal text-gray-500">({projects.length})</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project) => (
                  <div key={project.id} className="group border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 hover:border-primary-300">
                    {project.thumbnail_url ? (
                      <img 
                        src={project.thumbnail_url} 
                        alt={project.title}
                        className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-200"
                      />
                    ) : (
                      <div className="w-full h-40 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <div className="text-center">
                          <SafeIcon icon={FiUser} className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <span className="text-gray-400 text-sm">No thumbnail</span>
                        </div>
                      </div>
                    )}
                    <div className="p-5">
                      <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">{project.title}</h3>
                      <p className="text-sm text-gray-600 line-clamp-3 mb-4">{project.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{formatDate(project.updated_at)}</span>
                        <a
                          href={`/#/public/${project.id}`}
                          className="inline-flex items-center space-x-1 text-primary-600 hover:text-primary-700 text-sm font-medium group-hover:underline"
                        >
                          <span>View Presentation</span>
                          <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {projects.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiEye} className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Public Projects</h3>
            <p className="text-gray-600">This user hasn't shared any public projects yet.</p>
          </div>
        )}

        {/* Footer Note */}
        <div className="text-center py-8 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            This is a public profile on NuPitch - Create your own at{' '}
            <a href="/#/auth" className="text-primary-600 hover:text-primary-700 underline">
              nupitch.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default PublicProfile
